/**
 * Virtual breakout rooms (sandbox proof).
 *
 * Everyone stays in the class's main Jitsi conference for the whole session.
 * On the Jitsi side, a "room" is now only two things:
 *   1. which audio and video the bridge forwards to this client
 *      (audio subscription + receiver video constraints), and
 *   2. which Room model's tracks the UI shows.
 * Everything Node routes by roomId (chat, hands, reactions, status, slides,
 * whiteboard) works exactly as before. Node stays the source of truth for
 * membership (User.roomId). Anything sent through Jitsi itself (endpoint
 * messages, participant properties, moderation, recording status) now
 * reaches the whole class, not just one room.
 *
 * Set VIRTUAL_ROOMS to false to get the old leave/join behaviour back.
 */
import { markRaw, nextTick } from 'vue';

// TODO: fix these four import paths for your project (TypeScript will flag them).
import { Room } from './Room';
import { User } from './User';
import { MAIN_ROOM } from '../types';
import { getApp } from './App';
import type { Connection } from './Connection';

// Virtual rooms run only in the rooms named here (exact names, lowercase, as in the
// address bar). Every other session runs today's code. Everyone in a session
// (Jibri included) loads the same room name in the URL path, so they always agree
// on the mode.
const VIRTUAL_ROOM_NAMES: string[] = ['vrtest', 'lom_room_1', 'lom_room_27', 'lom_room_26', 'lom_room_23', 'lom_room_17', 'lom_room_13', 'lom_room_6', 'lom_room_4'];

const roomFromUrl = decodeURIComponent(location.pathname.split('/').filter(Boolean).pop() ?? '').toLowerCase();

export const VIRTUAL_ROOMS = VIRTUAL_ROOM_NAMES.includes(roomFromUrl);

const JitsiMeetJS = (window as any).JitsiMeetJS;

// Every track in the shared conference, local and remote. Each Room's `tracks`
// is a filtered view of this list.
const allTracks: any[] = [];

// Most recent speakers first, from DOMINANT_SPEAKER_CHANGED.
let speakerOrder: string[] = [];

// Last subscription sent, so the bridge is only messaged when something changed.
let lastKey = '';

/** Room.init(), main room only: start by receiving nothing until we know our room. */
export function onMainConferenceCreated(conference: any) {
  allTracks.length = 0;
  speakerOrder = [];
  lastKey = '';
  try {
    conference.setReceiverConstraints({ lastN: 5, defaultConstraints: { maxHeight: 0 } });
    if (typeof conference.setAudioSubscriptionMode === 'function') {
      conference.setAudioSubscriptionMode({ mode: 'None' });
    } else {
      console.error('[virtual] lib-jitsi-meet has no setAudioSubscriptionMode: audio will NOT be isolated');
    }
  } catch (e) {
    console.error('[virtual] initial subscription failed', e);
  }
}

/**
 * Point every Room model at the one shared conference. Breakout Room models
 * never join on their own, so they also mirror the main room's join state;
 * otherwise the UI treats the current breakout as "still joining".
 */
export function shareConference(conference: any) {
  const main = (Room as any).get(MAIN_ROOM) as Room | null;
  for (const room of Room.all() as Room[]) {
    if (room.conference !== conference) room.conference = conference;
    if (!main || room === main) continue;
    if (room.joined !== main.joined) room.joined = main.joined;
    if (room.joining !== main.joining) room.joining = main.joining;
    if (room.joinPromise !== main.joinPromise) room.joinPromise = main.joinPromise;
  }
}

/** Replaces Connection.switch() when VIRTUAL_ROOMS is on. No leave, no join. */
export function virtualSwitch(connection: Connection, name: string) {
  connection.switchPromise = (connection.switchPromise || Promise.resolve())
    .then(async () => {
      const main = connection.getConferenceForRoom(MAIN_ROOM);

      if (!main.joined) {
        connection.overlayMessage = 'Joining...';
        connection.switching = true;
        if (!main.joining) await main.join();
        await main.joinPromise;
      }
      shareConference(main.conference);

      if (connection.currentRoomName !== name) {
        const firstJoin = connection.currentRoomName === null;
        connection.currentRoomName = name;
        const room = (Room as any).get(name) as Room;

        if (!firstJoin) {
          await afterVirtualMove(room);
        } else if (name !== MAIN_ROOM) {
          // Reload while in a breakout: onConferenceJoined ran for main, so point chat at the breakout.
          const app = getApp();
          app.ws?.send(`[updateMe]${room.messages.length}`);
          if (app.currentChat !== app.currentPrivateChat) app.currentChat = name;
        }
      }

      refreshVirtualView(connection);
      connection.switching = false;
      connection.overlayMessage = '';
    })
    .catch((e: unknown) => {
      connection.overlayMessage = 'Failed to switch rooms. Refresh the page!';
      console.error('[virtual] switch failed', name, e);
    });

  return connection.switchPromise;
}

/** What onConferenceLeft + onConferenceJoined did on a real move, minus the leave and join. */
async function afterVirtualMove(room: Room) {
  const app = getApp();
  app.showVideo = false;
  app.isScreenShareEnabled = false;
  app.isVideoEnabled = false;
  app.isAudioEnabled = false;
  await room.refreshLocalTracks(); // the normal "mic/camera off" path, on the shared conference
  app.ws?.send(`[updateMe]${room.messages.length}`);
  if (app.currentChat !== app.currentPrivateChat) app.currentChat = room.name;
}

function roomOfTrack(track: any, currentRoomName: string): string | null {
  if (track.isLocal()) return currentRoomName;
  return User.getByJitsiId(track.getParticipantId())?.roomId ?? null;
}

/** Sort tracks into Room models and update what the bridge sends. Cheap; safe to call often. */
export function refreshVirtualView(connection: Connection) {
  const current = connection.currentRoomName;
  if (!current) return;

  for (const room of Room.all() as Room[]) {
    const want = allTracks.filter((t) => roomOfTrack(t, current) === room.name);
    const same = want.length === room.tracks.length && want.every((t, i) => room.tracks[i] === t);
    if (!same) room.tracks.splice(0, room.tracks.length, ...want);
  }

  // Camera icons follow the conference itself: an unmuted camera track means "on".
  // Everyone's tracks are in the one conference, so this is right for every room, and
  // [track] messages or track-removed events arriving out of order can't leave it stale.
  const localJitsiId = User.get(connection.id)?.jitsiId;
  const onCamera = new Set(
    allTracks
      .filter((t) => t.getType() === 'video' && t.getVideoType() === 'camera' && !t.isMuted())
      .map((t) => (t.isLocal() ? localJitsiId : t.getParticipantId())),
  );
  for (const u of User.all() as User[]) {
    const want = !!u.jitsiId && onCamera.has(u.jitsiId);
    if (u.isOnCamera !== want) u.isOnCamera = want;
  }

  const room = (Room as any).get(current) as Room | null;
  room?.setDisplay();
  room?.showVideoBox();

  const conference = (Room as any).get(MAIN_ROOM)?.conference;
  if (conference) applySubscriptions(conference, current);
}

/** Room.onDataChannelOpened(): the bridge channel is (re)open, so resend our subscription. */
export function resubscribe(connection: Connection) {
  lastKey = '';
  refreshVirtualView(connection);
}

function applySubscriptions(conference: any, roomName: string) {
  const members = (User.all() as User[]).filter((u) => u.roomId === roomName && u.jitsiId);
  const ids = new Set(members.map((u) => u.jitsiId));

  // Conventional source names cover people whose tracks haven't been signaled yet.
  const audio = new Set<string>(members.map((u) => `${u.jitsiId}-a0`));
  const video = new Set<string>(members.map((u) => `${u.jitsiId}-v0`));
  for (const t of allTracks) {
    if (t.isLocal() || !ids.has(t.getParticipantId())) continue;
    (t.getType() === 'audio' ? audio : video).add(t.getSourceName());
  }

  const a = [...audio].sort();
  const v = [...video].sort();
  const key = `${roomName}|${a.join(',')}|${v.join(',')}`;
  if (key === lastKey) return;
  lastKey = key;

  try {
    conference.setAudioSubscriptionMode?.({ mode: 'Include', list: a });
    conference.setReceiverConstraints({
      lastN: 5,
      defaultConstraints: { maxHeight: 0 },
      constraints: Object.fromEntries(v.map((s) => [s, { maxHeight: 180 }])),
    });
    console.log('[virtual] room', roomName, 'audio', a, 'video', v);
  } catch (e) {
    lastKey = '';
    console.error('[virtual] subscription failed', e);
  }
}

/** Room.onTrackAdded() when VIRTUAL_ROOMS is on. */
export function onVirtualTrackAdded(room: Room, track: any) {
  const t = markRaw(track);
  if (!allTracks.includes(t)) allTracks.push(t);
  const refresh = () => refreshVirtualView(room.connection);

  if (t.getType() === 'video') {
    t.addEventListener(JitsiMeetJS.events.track.TRACK_VIDEOTYPE_CHANGED, () => {
      // Same trick as the original handler: take it out, put it back next tick so the UI notices.
      for (const r of Room.all() as Room[]) {
        const i = r.tracks.indexOf(t);
        if (i >= 0) r.tracks.splice(i, 1);
      }
      nextTick(refresh);
    });
  }
  t.addEventListener(JitsiMeetJS.events.track.TRACK_MUTE_CHANGED, refresh);
  refresh();
}

/** Room.onTrackRemoved() when VIRTUAL_ROOMS is on. */
export function onVirtualTrackRemoved(room: Room, track: any) {
  const id = track.getTrackId();
  const i = allTracks.findIndex((t) => t.getTrackId() === id);
  if (i >= 0) allTracks.splice(i, 1);

  // Camera icons are recomputed from the conference in refreshVirtualView.
				 
		   
   
  refreshVirtualView(room.connection);
}

/** Room.onDominantSpeakerChanged(): keep the bridge's speaker order. */
export function setSpeakerOrder(id: string, previous?: string[]) {
  speakerOrder = [id, ...(previous ?? []).filter((p) => p !== id)];
}

/** Most recent speaker who is in this room (the bridge's own pick is conference-wide). */
export function dominantSpeakerIn(roomName: string): string | undefined {
  return speakerOrder.find((id) => User.getByJitsiId(id)?.roomId === roomName);
}